import * as React from "react";

import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { flexRender } from "@tanstack/react-table";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { Attribute } from "../../hooks/filtering/sharedTypes";
import { Spinner } from "../Spinner/Spinner.styles";
import { HeaderContentsWrap, RowContentsWrap } from "./ui/BaseTableStyles";
import { groupInterjectionsByIndex } from "./ui/interjectedRows/InterjectedRow.model";
import { TableInterjectedRow } from "./ui/interjectedRows/TableInterjectedRow";
import { useBaseTable } from "./useBaseTable";
import { SpaceToPreviewMetaProvider } from "./useSpaceToPreview";

interface BaseTableProps<TData> extends ReturnType<typeof useBaseTable<TData>> {
  footer?: React.ReactNode;
}

export function BaseTable<TData>({
  table,
  gridTemplateColumns,
  onContainerScroll,
  containerRef,
  renderRowLinkPath,
  spaceToPreview,
  selectable,
  onRowClick,
  validAttributes,
  withBorder,
  isFetchingNext,
  footer,
  flaggedIds,
  interjections,
}: BaseTableProps<TData>) {
  const { onMouseEnterContainer, onMouseLeaveContainer, onMouseOverRow } = spaceToPreview;

  const headerRef = React.useRef<HTMLDivElement | null>(null);

  const dataRows = table.getRowModel().rows;

  const interjectionsByIndex = groupInterjectionsByIndex(interjections?.rows, dataRows.length);

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <SpaceToPreviewMetaProvider {...spaceToPreview}>
          <div
            onMouseLeave={onMouseLeaveContainer}
            style={{
              position: "relative",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflowX: "hidden",
            }}
          >
            <div
              id="table-container"
              ref={containerRef}
              onMouseEnter={onMouseEnterContainer}
              onScroll={() => onContainerScroll()}
              style={{
                position: "relative",
                overflow: "overlay",
                flex: 1,
                overflowX: "hidden",
                outline: "none",
                userSelect: "none",
              }}
            >
              <div
                style={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns,
                  paddingBottom: "6rem",
                  paddingLeft: "3rem",
                  paddingRight: "3rem",
                }}
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <HeaderContentsWrap
                    $selected={table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()}
                    key={`${headerGroup.id}-contents`}
                    $tableSelectionEnabled={selectable}
                  >
                    {headerGroup.headers.map((header) => {
                      // zIndex 1 is default for all headers, zIndex 2 makes the popover work

                      return (
                        <div
                          ref={headerRef}
                          key={header.id}
                          style={{ position: "sticky", top: 0, zIndex: 4 /* isSelectionHeader ? 2 : 1 */ }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      );
                    })}
                  </HeaderContentsWrap>
                ))}
                {dataRows.map((row) => {
                  const linkPath = renderRowLinkPath?.(row.original);

                  const isFlagged = flaggedIds ? flaggedIds.includes((row.original as { id: string }).id) : false;
                  const dimmed = flaggedIds ? flaggedIds.length > 0 && !isFlagged : false;
                  const interjectedAfter = interjectionsByIndex.get(row.index);
                  return (
                    <React.Fragment key={`${row.id}-group`}>
                      <RowItem
                        path={linkPath}
                        flagged={isFlagged}
                        dimmed={dimmed}
                        selected={row.getIsSelected()}
                        onRowClick={onRowClick ? () => onRowClick(row.original) : undefined}
                        key={`${row.id}-contents`}
                        onMouseOver={(e) => {
                          onMouseOverRow(e, row.index);
                        }}
                        tableSelectionEnabled={selectable}
                        originalRow={row.original}
                        validAttributes={validAttributes}
                        withBorder={withBorder}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <React.Fragment key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </React.Fragment>
                        ))}
                      </RowItem>
                      {interjectedAfter?.map((interjectedRow) => (
                        <TableInterjectedRow
                          key={interjectedRow.id}
                          row={interjectedRow}
                          onDismiss={interjections?.onDismiss ?? (() => undefined)}
                          onFeedbackSubmit={interjections?.onFeedbackSubmit}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}
              </div>
              {footer}
              {isFetchingNext && (
                <Flex
                  $justifyContent="center"
                  $alignItems="center"
                  style={{ position: "fixed", bottom: "0.4rem", left: "50%" }}
                >
                  <Spinner style={{ width: "2.8rem", height: "2.8rem" }} />
                </Flex>
              )}
            </div>
          </div>
        </SpaceToPreviewMetaProvider>
      </DndProvider>
    </>
  );
}

class RowItem extends React.Component<{
  children: React.ReactNode;
  path: string | undefined;
  flagged: boolean;
  dimmed: boolean;
  selected: boolean;
  onMouseOver?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  tableSelectionEnabled: boolean;
  onRowClick: (() => void) | undefined;
  // eslint-disable-next-line
  originalRow: any;
  validAttributes?: Attribute[];
  withBorder?: boolean;
}> {
  // eslint-disable-next-line
  shouldComponentUpdate(nextProps: any) {
    if (!nextProps || !this.props) return false;

    return (
      nextProps.selected !== this.props.selected ||
      nextProps.flagged !== this.props.flagged ||
      nextProps.dimmed !== this.props.dimmed ||
      nextProps.path !== this.props.path ||
      nextProps.tableSelectionEnabled !== this.props.tableSelectionEnabled ||
      nextProps.validAttributes !== this.props.validAttributes ||
      nextProps.originalRow?.status !== this.props.originalRow?.status ||
      nextProps.originalRow?.priority !== this.props.originalRow?.priority ||
      nextProps.originalRow?.assignee?.id !== this.props.originalRow?.assignee?.id ||
      nextProps.originalRow?.prettyTitle !== this.props.originalRow?.prettyTitle ||
      nextProps.originalRow?.tags !== this.props.originalRow?.tags ||
      nextProps.originalRow?.linked_issues_count !== this.props.originalRow?.linked_issues_count ||
      nextProps.originalRow?.linked_crash_groups_count !== this.props.originalRow?.linked_crash_groups_count ||
      nextProps.originalRow?.issue_reported_time !== this.props.originalRow?.issue_reported_time ||
      nextProps.originalRow?.showNotificationIndicator !== this.props.originalRow?.showNotificationIndicator ||
      nextProps.originalRow?.notificationType !== this.props.originalRow?.notificationType ||
      nextProps.originalRow?.description !== this.props.originalRow?.description ||
      nextProps.originalRow?.url !== this.props.originalRow.url ||
      nextProps.originalRow?.method !== this.props.originalRow.method ||
      nextProps.originalRow?.request_type !== this.props.originalRow.request_type ||
      nextProps.originalRow?.content_type !== this.props.originalRow.content_type ||
      nextProps.originalRow?.duration !== this.props.originalRow.duration ||
      nextProps.originalRow?.timestamp !== this.props.originalRow.timestamp
    );
  }

  render() {
    const { selected, flagged, dimmed, path, onMouseOver, tableSelectionEnabled, onRowClick, children, withBorder } =
      this.props;

    return (
      <MemoRow
        selected={selected}
        flagged={flagged}
        dimmed={dimmed}
        path={path}
        onMouseOver={onMouseOver}
        tableSelectionEnabled={tableSelectionEnabled}
        onRowClick={onRowClick}
        withBorder={withBorder}
      >
        {children}
      </MemoRow>
    );
  }
}

const MemoRow = React.memo(
  ({
    children,
    selected,
    flagged,
    dimmed,
    path,
    onMouseOver,
    tableSelectionEnabled,
    onRowClick,
    withBorder,
  }: {
    children: React.ReactNode;
    path: string | undefined;
    selected: boolean;
    flagged: boolean;
    dimmed: boolean;
    onMouseOver?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    tableSelectionEnabled: boolean;
    onRowClick: (() => void) | undefined;
    withBorder?: boolean;
  }) => {
    return (
      <LinkRowWrap
        selected={selected}
        flagged={flagged}
        dimmed={dimmed}
        path={path}
        onMouseOver={onMouseOver}
        tableSelectionEnabled={tableSelectionEnabled}
        onRowClick={onRowClick}
        withBorder={withBorder}
      >
        {children}
      </LinkRowWrap>
    );
  },
);

MemoRow.displayName = "MemoRow";

function LinkRowWrap({
  children,
  selected,
  flagged,
  dimmed,
  path,
  onMouseOver,
  tableSelectionEnabled,
  onRowClick,
  withBorder,
}: {
  children: React.ReactNode;
  path: string | undefined;
  selected: boolean;
  flagged: boolean;
  dimmed: boolean;
  onMouseOver?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  tableSelectionEnabled: boolean;
  onRowClick: (() => void) | undefined;
  withBorder?: boolean;
}) {
  if (path)
    return (
      <RowContentsWrap
        to={path}
        $selected={selected}
        $flagged={flagged}
        $dimmed={dimmed}
        onMouseOver={onMouseOver}
        $tableSelectionEnabled={tableSelectionEnabled}
      >
        {children}
      </RowContentsWrap>
    );

  return (
    <RowContentsWrap
      as="div"
      $selected={selected}
      $flagged={flagged}
      $dimmed={dimmed}
      onMouseOver={onMouseOver}
      onClick={onRowClick}
      $tableSelectionEnabled={tableSelectionEnabled}
      $withBorder={withBorder}
    >
      {children}
    </RowContentsWrap>
  );
}
